import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { UploadCloud, File as FileIcon, Loader2, AlertCircle, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

interface FileDropzoneProps {
  onDrop: (files: File[]) => void;
  isLoading?: boolean;
}

export function FileDropzone({ onDrop, isLoading = false }: FileDropzoneProps) {
  const { language } = useLanguage();
  const isEs = (language as string) === 'es';
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  // Check if filename follows the recommended convention (soft check — non-blocking)
  const isRecommendedName = (name: string): boolean => {
    // Accepts: "XXX - DACAMS Data Retrieval.xlsx", with optional version suffix
    // e.g. "MAD - DACAMS Data Retrieval.xlsx", "MAD - DACAMS Data Retrieval v2.xlsx"
    const regex = /^[A-Z]{3,4}\s*-\s*DACAMS Data Retrieval(\s*v?\d+)?\.(xlsx|csv)$/i;
    return regex.test(name);
  };

  const handleDrop = useCallback((acceptedFiles: File[], fileRejections: any[]) => {
    setError(null);
    setWarning(null);

    // 1. Hard error: wrong file format (not excel/csv — blocked by react-dropzone)
    if (fileRejections.length > 0) {
      setError(isEs
        ? 'Formato no admitido. Solo se permiten archivos .xlsx y .csv'
        : 'Unsupported format. Only .xlsx and .csv files are allowed');
      return;
    }

    if (acceptedFiles?.length > 0) {
      const file = acceptedFiles[0];

      // 2. Soft warning: name doesn't follow convention (non-blocking — file is still uploaded)
      if (!isRecommendedName(file.name)) {
        setWarning(
          isEs
            ? `Nombre no estándar. Se recomienda: "XXX - DACAMS Data Retrieval.xlsx". El archivo será procesado igualmente si el contenido es correcto.`
            : `Non-standard name. Recommended: "XXX - DACAMS Data Retrieval.xlsx". File will be processed as long as the content is correct.`
        );
      }

      // Always proceed with upload (content validation happens in backend)
      onDrop(acceptedFiles);
    }
  }, [onDrop]);

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    onDrop: handleDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    disabled: isLoading
  });

  return (
    <div className="space-y-2">
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors relative
          ${error
            ? 'border-red-500 bg-red-50'
            : warning
              ? 'border-amber-400 bg-amber-50/50'
              : isDragActive
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }
          ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />

        <div className="flex flex-col items-center justify-center space-y-3">
          {isLoading ? (
            <>
              <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
              <p className="text-sm text-[var(--text-tertiary)]">{isEs ? 'Procesando archivo...' : 'Processing file...'}</p>
            </>
          ) : acceptedFiles.length > 0 && !error ? (
            <>
              <FileIcon className="h-10 w-10 text-green-500" />
              <p className="text-sm font-medium text-gray-900">{acceptedFiles[0].name}</p>
              <p className="text-xs text-green-600">{isEs ? 'Archivo aceptado' : 'File accepted'}</p>
            </>
          ) : (
            <>
              {error ? (
                <AlertCircle className="h-10 w-10 text-red-500" />
              ) : (
                <UploadCloud className="h-10 w-10 text-[var(--text-secondary)]" />
              )}

              <div className="text-sm text-gray-600">
                <span className="font-semibold text-blue-600 hover:text-blue-500">{isEs ? 'Haz clic para subir' : 'Click to upload'}</span>
                {' '}{isEs ? 'o arrastra el archivo' : 'or drag and drop'}
              </div>
              <p className="text-xs text-[var(--text-tertiary)]">
                {isEs ? 'Archivos .xlsx o .csv con datos AODB' : '.xlsx or .csv files with AODB data'}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Hard error (format rejection) */}
      {error && (
        <div className="flex items-start gap-2 p-2.5 rounded-lg bg-red-50 border border-red-200">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-600 font-medium">{error}</p>
        </div>
      )}

      {/* Soft warning (non-standard name — file still accepted) */}
      {warning && !error && (
        <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700">{warning}</p>
        </div>
      )}
    </div>
  );
}